import { Download } from 'lucide-react';
import { useState } from 'react';

import { urlDeArquivo } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  busca?: string;
  tenantId?: string;
  ativo?: 'true' | 'false';
}

/**
 * ⚠️ Link (`<a download>`), não `fetch` — a rota devolve `text/csv` e o
 * `httpClient` faz `JSON.parse` incondicional, que quebraria no CSV (mesma
 * armadilha documentada em `BotaoDeExportar.tsx`, de análises).
 *
 * "Extrair completo" ignora TODOS os filtros da tela — é o dump inteiro do
 * banco, de propósito. "Por período" é o oposto: aplica o recorte da tela
 * (busca/ambiente/status) e ainda limita pelo intervalo de datas.
 */
export function ExportarUsuariosDialogo({ aberto, aoFechar, busca, tenantId, ativo }: Props) {
  const [modo, setModo] = useState<'completo' | 'periodo'>('completo');
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');

  const parametros = new URLSearchParams();
  if (modo === 'periodo') {
    if (busca) parametros.set('busca', busca);
    if (tenantId) parametros.set('tenantId', tenantId);
    if (ativo) parametros.set('ativo', ativo);
    if (desde) parametros.set('desde', desde);
    if (ate) parametros.set('ate', ate);
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Exportar usuários</DialogTitle>
          <DialogDescription>Escolha entre a base inteira ou um recorte por período.</DialogDescription>
        </DialogHeader>

        <Tabs value={modo} onValueChange={(v) => setModo(v as 'completo' | 'periodo')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="completo">Extrair completo</TabsTrigger>
            <TabsTrigger value="periodo">Por período</TabsTrigger>
          </TabsList>

          <TabsContent value="completo">
            <p className="text-sm text-muted-foreground">
              Exporta TODOS os usuários da base, ignorando busca, ambiente e status da tela.
            </p>
          </TabsContent>

          <TabsContent value="periodo" className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Aplica o recorte da tela (busca, ambiente e status) e limita aos usuários criados no
              intervalo (os dois lados são opcionais).
            </p>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                aria-label="Criado a partir de"
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm">até</span>
              <Input
                type="date"
                value={ate}
                onChange={(e) => setAte(e.target.value)}
                aria-label="Criado até"
                className="flex-1"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button asChild>
            <a
              href={urlDeArquivo(`/v1/admin/usuarios/exportar.csv?${parametros.toString()}`)}
              download
              onClick={aoFechar}
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
